import React, { useState } from 'react';
import { observable, flow } from 'mobx';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import dayjs from 'dayjs';

import { Colours, Sizes, Paragraph, noticeError } from '@ratehub/base-ui';

import AnimatedDeleteContainer from './AnimatedDeleteContainer';
import SavedDocumentsRowInsurance from './SavedDocumentsRowInsurance';
import QuoteSummary from './QuoteSummary';
import QuoteDetailsAuto from './QuoteDetailsAuto';


function SavedDocumentsRowAuto({ model, document, onRemoveRow }) {
    const intl = useIntl();
    const [ autoModel ] = useState(
        model && document
            ? () => createAutoModel(model, document)
            : null,
    );

    return (
        <Container
            isDeleted={autoModel?.isDeletedFromBackend}
            isCompleted={autoModel?.isCompleted}
        >
            <Choose>
                <When condition={!!autoModel}>
                    <AnimatedDeleteContainer
                        className="document"
                        canDelete={!autoModel.isCompleted}
                        shouldShowSpinner={autoModel.isDeleting}
                        onDelete={autoModel.deleteDocument}
                        isItemDeleted={autoModel.isDeletedFromBackend}
                        onDeleteComplete={onRemoveRow}
                        showErrorMessage={autoModel.hasFailedDeletion}
                        confirmationTitle={Messages.DELETE_TITLE}
                        confirmText={Messages.CONFIRM_DELETE}
                        rejectText={Messages.REJECT_DELETE}
                        successTitle={intl.formatMessage(Messages.SUCCESSFUL_DELETE)}
                        errorTitle={intl.formatMessage(Messages.ERROR_DELETE)}
                    >
                        <SavedDocumentsRowInsurance
                            pillMessage={!autoModel.isCompleted
                                && (
                                    <FormattedMessage
                                        {...Messages.EXPIRES_IN_DAYS}
                                        values={{ amount: autoModel.daysLeft }}
                                    />
                                )
                            }
                            href={autoModel.isCompleted ? undefined : autoModel.resumeHREF}
                            hrefMessage={Messages.RESUME_HREF}
                            SummaryComponent={(
                                <QuoteSummary
                                    className="quote-summary"
                                    title={autoModel.allVehicleNames}
                                    isCompleted={autoModel.isCompleted}
                                    monthlyRate={autoModel.monthlyRate}
                                    yearlyRate={autoModel.yearlyRate}
                                />
                            )}
                            details={autoModel.isCompleted
                                ? [
                                    { label: Messages.INSURANCE_PROVIDER_LABEL, value: autoModel.insurerName },
                                    { label: Messages.QUOTE_COMPLETED_LABEL, value: autoModel.dateLastSaved },
                                ]
                                : [
                                    { label: Messages.QUOTE_STARTED_LABEL, value: autoModel.dateCreated },
                                ]}
                            data-name="Dashboard-resumeQuote"
                            isCompleted={!!autoModel.isCompleted}
                        >
                            <If condition={autoModel.isCompleted}>
                                <QuoteDetailsAuto
                                    vehicles={autoModel.vehicles}
                                    brokerName={autoModel.brokerName}
                                    brokerPhone={autoModel.brokerPhone}
                                />
                            </If>
                        </SavedDocumentsRowInsurance>
                    </AnimatedDeleteContainer>
                </When>
                <Otherwise>
                    <Paragraph
                        className="no-document"
                        size="medium"
                        message={Messages.NO_QUOTES}
                    />
                </Otherwise>
            </Choose>
        </Container>
    );
}

SavedDocumentsRowAuto.propTypes = {
    model: PropTypes.object,
    document: PropTypes.object,
    onRemoveRow: PropTypes.func,
};

SavedDocumentsRowAuto.defaultProps = {
    model: undefined,
    document: undefined,
    onRemoveRow: undefined,
};

const EXPIRY_DATE_LIMIT = 30;
const RESUME_URL_EN = '/insurance/car-quotes/resume';

function createAutoModel(model, document) {
    const self = observable({
        model,
        document,

        isDeleting: false,
        isDeletedFromBackend: false,
        hasFailedDeletion: false,

        get quote() {
            return self.document.quote;
        },
        get application() {
            return self.document.application;
        },
        get providerStore() {
            return self.model.context.insuranceProviderStore;
        },
        get insurer(){
            return self.providerStore.getInsurerById(self.quote?.insurerId);
        },
        get insurerName() {
            return self.insurer?.name ?? null;
        },
        get broker() {
            return self.providerStore.getBrokerById(self.quote?.brokerId);
        },
        // Broker can be null if quote is direct, we default values to insurer if this is the case
        get brokerName() {
            return (self.broker ? self.broker?.name : self.insurer?.name) ?? null;
        },
        get brokerPhone() {
            return (self.broker ? self.broker?.phoneNumber : self.insurer?.phoneNumber) ?? null;
        },
        get vehicles() {
            return self.application.vehicles.map(vehicle => ({
                code: vehicle.code,
                fullName: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
                policyLength: self.isCompleted ? self.quote.termInMonths : null,
                comprehensive: self.isCompleted ? Number(vehicle.coverage.comprehensive) : null,
                collision: self.isCompleted ? Number(vehicle.coverage.collision) : null,
                liability: self.isCompleted ? Number(vehicle.coverage.liability) : null,
                lossOfUse: self.isCompleted ? vehicle.coverage.lossOfUse : null, // boolean
                accidentForgiveness: self.isCompleted ? vehicle.coverage.accidentForgiveness : null, // boolean
                removingDepreciation: self.isCompleted ? vehicle.coverage.removingDepreciation : null, // boolean
                rentalCarCoverage: self.isCompleted ? vehicle.coverage.rentalCarCoverage : null, // boolean
            }));
        },
        get allVehicleNames() {
            return self.vehicles.map(v => v.fullName);
        },
        get isCompleted() {
            return self.quote && self.quote.id !== null;
        },
        get dateCreated() {
            // very old document might not have createdAt/updatedAt, they only have dateCreated
            return dayjs(self.document.createdAt ?? self.document.dateCreated).format('MMM D, YYYY');
        },
        get dateLastSaved() {
            // very old document might not have createdAt/updatedAt, they only have dateCreated
            return dayjs(self.document.userUpdatedAt ?? self.document.updatedAt ?? self.document.createdAt ?? self.document.dateCreated).format('MMM D, YYYY');
        },
        get daysLeft() {
            return self.isCompleted
                ? null
                : dayjs(self.document.userUpdatedAt ?? self.document.updatedAt ?? self.document.createdAt ?? self.document.dateCreated).clone().add(EXPIRY_DATE_LIMIT, 'days')
                    .diff(dayjs(), 'days') + 1;
        },
        get monthlyRate() {
            return self.isCompleted
                ? self.quote.monthlyRate
                : null;
        },
        get yearlyRate() {
            return self.isCompleted
                ? self.quote.yearlyRate
                : null;
        },
        get resumeHREF() {
            return `${RESUME_URL_EN}/${self.document.id}`;
        },

        deleteDocument: flow(function* (){
            if (self.isDeletedFromBackend) {
                noticeError(new Error(`[SavedDocumentsRowAuto] Attempted to delete document ${self.document.id}, which is already deleted`));
                return null;
            }

            self.isDeleting = true;
            self.hasFailedDeletion = false;

            try {
                yield self.model.context.sessionStore.deleteDocument(self.document.id);
                self.isDeletedFromBackend = true;
            } catch (error) {
                // Error is noticed in sessionStore.deleteDocument
                self.isDeleting = false;
                self.hasFailedDeletion = true;
            }

            self.isDeleting = false;
        }),
    });

    return self;
}

const Container = styled.div`
    > .no-document {
        text-align: center;
        margin: ${Sizes.SPACING.ONE_AND_A_HALF};
        color: ${Colours.STONE_DARKEST};
    }
    .document {
        position: relative;
        ${props => !props.isDeleted && `
            margin: ${Sizes.SPACING.ONE} ${Sizes.SPACING.ONE_AND_A_HALF};
        `}
        ${props => props.isCompleted && `
            > .quote-summary {
                margin-bottom: 0;
            }
        `
}
`;

const Messages = defineMessages({
    NO_QUOTES: {
        id: 'Accounts.autoQuoteHistoryTable.noQuotes',
        defaultMessage: 'You have no auto insurance quotes in progress.',
    },
    START_QUOTE: {
        id: 'Accounts.autoQuoteHistoryTable.startQuote',
        defaultMessage: 'Start my auto quote',
    },
    TOOLTIP: {
        id: 'Accounts.autoQuoteHistoryTable.tooltip',
        defaultMessage: 'Find all your Auto Insurance Quotes here',
    },
    DELETE_TITLE: {
        id: 'Accounts.autoQuoteHistoryTable.deleteConfirmationTitle',
        defaultMessage: 'Are you sure you want to delete this quote?',
    },
    CONFIRM_DELETE: {
        id: 'Accounts.autoQuoteHistoryTable.confirmDelete',
        defaultMessage: 'Yes, delete quote',
    },
    REJECT_DELETE: {
        id: 'Accounts.autoQuoteHistoryTable.cancelDelete',
        defaultMessage: 'Cancel, keep quote',
    },
    SUCCESSFUL_DELETE: {
        id: 'Accounts.autoQuoteHistoryTable.deleteSuccess',
        defaultMessage: 'Quote deleted successfully!',
    },
    ERROR_DELETE: {
        id: 'Accounts.autoQuoteHistoryTable.deleteFailure',
        defaultMessage: 'Unable to delete quote at this time. Please retry.',
    },
    RESUME_HREF: {
        id: 'Dashboard.QuoteHistoryTableAuto.resumeQuote',
        defaultMessage: 'Resume quote',
    },
    INSURANCE_PROVIDER_LABEL: {
        id: 'Dashboard.QuoteHistoryTableAuto.InsuranceProvider',
        defaultMessage: 'Insurance provider:',
    },
    QUOTE_COMPLETED_LABEL: {
        id: 'Dashboard.QuoteHistoryTableAuto.QuoteCompleted',
        defaultMessage: 'Quote completed:',
    },
    QUOTE_STARTED_LABEL: {
        id: 'Dashboard.QuoteHistoryTableAuto.QuoteStarted',
        defaultMessage: 'Quote started:',
    },
    EXPIRES_IN_DAYS: {
        id: 'Dashboard.QuoteHistoryTableAuto.expiresInDays',
        defaultMessage: 'Expires in <strong>{amount} days</strong>',
    },
});

export default observer(SavedDocumentsRowAuto);
